import { useId, useState, type CSSProperties, type ReactNode } from 'react';
import type {
  PrintableAreaConfig,
  ProductConfig,
  ProductViewId,
  ViewportState,
} from '../domain/types';
import {
  resolveGarmentVisualScale,
  resolvePrintableArea,
  resolveProductVariant,
  resolveSelectableSizes,
  resolveSizeProfile,
} from '../products';
import type { GarmentVisualScale } from '../products';

export { resolveGarmentVisualScale } from '../products';
export type { GarmentVisualScale } from '../products';

interface GarmentMockupProps {
  product: ProductConfig;
  colour: string;
  variantId?: string;
  size?: string;
  viewId?: ProductViewId;
  viewport?: ViewportState;
  viewportOrigin?: { xPct: number; yPct: number };
  priority?: boolean;
  className?: string;
  children?: ReactNode;
}

interface GarmentPalette {
  base: string;
  edge: string;
  highlight: string;
  deep: string;
  outline: string;
  seam: string;
}

const TSHIRT_BODY =
  'M155 106 210 78c13 19 30 28 50 28s37-9 50-28l55 28 92 62-50 82-51-29 7 325H157l7-325-51 29-50-82 92-62Z';
const HOODIE_BODY =
  'M170 108 132 122 54 188l47 262 67-12-8-196-9 298h218l-9-298-8 196 67 12 47-262-78-66-38-14c-22 18-50 28-90 28s-68-10-90-28Z';
const LONG_SLEEVE_BODY =
  'M158 104 211 78c13 19 30 28 49 28s36-9 49-28l53 26 104 69-48 270-67-12 8-193 4 302H157l4-302 8 193-67 12-48-270 104-69Z';

export function GarmentMockup({
  product,
  colour,
  variantId,
  size,
  viewId = 'front',
  viewport,
  viewportOrigin,
  priority = false,
  className = '',
  children,
}: GarmentMockupProps) {
  const reactId = useId().replace(/:/g, '');
  const id = `garment-${product.garmentType}-${reactId}`;
  const palette = createPalette(colour);
  const isBack = viewId === 'back';
  const selectedVariant = resolveProductVariant(product, variantId) ?? product.variants[0]!;
  const selectableSizes = resolveSelectableSizes(selectedVariant);
  const fallbackSize = selectableSizes.includes('M') ? 'M' : selectableSizes[0]!;
  const selectedSize = selectableSizes.includes(size ?? '') ? size! : fallbackSize;
  const profile = resolveSizeProfile(product, selectedVariant.id, selectedSize);
  const area = resolvePrintableArea(product, selectedVariant.id, selectedSize, viewId);
  const visualScale = resolveGarmentVisualScale(product, profile);
  const photoSource = /\.(?:png|webp)(?:[?#].*)?$/i.test(area.mockupImage)
    ? area.mockupImage
    : undefined;
  const [failedPhoto, setFailedPhoto] = useState<string>();
  const showPhoto = Boolean(photoSource && failedPhoto !== photoSource);
  const garmentLuminance = luminance(colour);
  const garmentTone =
    garmentLuminance > 0.92 ? 'white' : garmentLuminance > 0.58 ? 'light' : 'dark';
  const layerStyle = {
    '--garment-scale-x': String(visualScale.x),
    '--garment-scale-y': String(visualScale.y),
  } as CSSProperties & Record<`--${string}`, string>;
  const viewportStyle = resolveGarmentViewportStyle(viewport, viewportOrigin);

  return (
    <div
      className={`garment-mockup ${className}`}
      data-garment-size={selectedSize}
      data-garment-tone={garmentTone}
    >
      <div className="garment-visual">
        <div className="garment-viewport-layer" style={viewportStyle}>
          <div className="garment-layer" style={layerStyle}>
            {showPhoto ? (
              <PhotoGarment
                source={photoSource!}
                colour={colour}
                label={`${product.name}, ${selectedSize}, ${viewId}`}
                priority={priority}
                onError={() => setFailedPhoto(photoSource)}
              />
            ) : (
              <svg
                viewBox="0 0 520 620"
                preserveAspectRatio="xMidYMid meet"
                role="img"
                aria-label={`${product.name}, ${selectedSize}, ${viewId}`}
              >
                <defs>
                  <linearGradient id={`${id}-fabric`} x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor={palette.edge} />
                    <stop offset="24%" stopColor={palette.base} />
                    <stop offset="52%" stopColor={palette.highlight} />
                    <stop offset="78%" stopColor={palette.base} />
                    <stop offset="100%" stopColor={palette.edge} />
                  </linearGradient>
                  <linearGradient id={`${id}-depth`} x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#fff" stopOpacity=".035" />
                    <stop offset="58%" stopColor="#fff" stopOpacity="0" />
                    <stop offset="100%" stopColor="#000" stopOpacity=".045" />
                  </linearGradient>
                  <filter id={`${id}-shadow`} x="-20%" y="-15%" width="140%" height="145%">
                    <feDropShadow
                      dx="0"
                      dy="7"
                      stdDeviation="9"
                      floodColor="#111216"
                      floodOpacity=".1"
                    />
                  </filter>
                </defs>

                {product.garmentType === 't-shirt' && (
                  <g filter={`url(#${id}-shadow)`}>
                    <path
                      d={TSHIRT_BODY}
                      fill={`url(#${id}-fabric)`}
                      stroke={palette.outline}
                      strokeWidth="1.6"
                      strokeLinejoin="round"
                    />
                    <path d={TSHIRT_BODY} fill={`url(#${id}-depth)`} pointerEvents="none" />
                    <TshirtCollar palette={palette} isBack={isBack} />
                    <path
                      d="m155 106 48-23m162 23-48-23M113 250l51-29m243 29-51-29M159 531h202"
                      fill="none"
                      stroke={palette.seam}
                      strokeWidth="1.6"
                      strokeOpacity=".6"
                    />
                    <path
                      d="M181 228c-6 99-5 196-2 289m160-289c6 99 5 196 2 289"
                      fill="none"
                      stroke={palette.deep}
                      strokeWidth="4"
                      strokeLinecap="round"
                      strokeOpacity=".08"
                    />
                  </g>
                )}

                {product.garmentType === 'hoodie' && (
                  <g filter={`url(#${id}-shadow)`}>
                    <path
                      d="M185 120c-7-48 25-92 75-92s82 44 75 92c-20 25-45 38-75 38s-55-13-75-38Z"
                      fill={`url(#${id}-fabric)`}
                      stroke={palette.outline}
                      strokeWidth="1.6"
                    />
                    {!isBack && (
                      <path
                        d="M203 108c5-36 25-57 57-57s52 21 57 57c-16 17-35 26-57 26s-41-9-57-26Z"
                        fill={palette.deep}
                        stroke={palette.seam}
                        strokeWidth="1.15"
                      />
                    )}
                    <path
                      d={HOODIE_BODY}
                      fill={`url(#${id}-fabric)`}
                      stroke={palette.outline}
                      strokeWidth="1.6"
                      strokeLinejoin="round"
                    />
                    <path d={HOODIE_BODY} fill={`url(#${id}-depth)`} pointerEvents="none" />
                    {isBack ? (
                      <path
                        d="M190 116c19 19 42 29 70 29s51-10 70-29M260 36v105"
                        fill="none"
                        stroke={palette.seam}
                        strokeWidth="1.5"
                        strokeOpacity=".55"
                      />
                    ) : (
                      <>
                        <path
                          d="M247 124v147m26-147v147"
                          stroke={palette.seam}
                          strokeWidth="1.5"
                          strokeOpacity=".7"
                        />
                        <circle cx="247" cy="132" r="3.2" fill={palette.seam} />
                        <circle cx="273" cy="132" r="3.2" fill={palette.seam} />
                        <path
                          d="M190 378c37-21 103-21 140 0l-12 74H202l-12-74Z"
                          fill={palette.deep}
                          fillOpacity=".1"
                          stroke={palette.seam}
                          strokeWidth="1.35"
                          strokeOpacity=".72"
                        />
                        <path
                          d="M260 374v77"
                          stroke={palette.seam}
                          strokeWidth="1"
                          strokeOpacity=".42"
                        />
                      </>
                    )}
                    <path
                      d="M153 520h214m7-278 9 278m-223-278-9 278M101 450l67-12m251 12-67-12"
                      fill="none"
                      stroke={palette.seam}
                      strokeWidth="1.6"
                      strokeOpacity=".55"
                    />
                    <path
                      d="m99 430 67-12 3 25-68 13zm322 0-67-12-3 25 68 13z"
                      fill={palette.deep}
                      fillOpacity=".1"
                    />
                    <path d="M151 520h218v20H151z" fill={palette.deep} fillOpacity=".09" />
                  </g>
                )}

                {product.garmentType === 'long-sleeve' && (
                  <g filter={`url(#${id}-shadow)`}>
                    <path
                      d={LONG_SLEEVE_BODY}
                      fill={`url(#${id}-fabric)`}
                      stroke={palette.outline}
                      strokeWidth="1.6"
                      strokeLinejoin="round"
                    />
                    <path d={LONG_SLEEVE_BODY} fill={`url(#${id}-depth)`} pointerEvents="none" />
                    <TshirtCollar palette={palette} isBack={isBack} />
                    <path
                      d="m158 104 46-22m158 22-46-22M102 443l67-12m249 12-67-12M159 531h202"
                      fill="none"
                      stroke={palette.seam}
                      strokeWidth="1.6"
                      strokeOpacity=".58"
                    />
                    <path
                      d="m103 428 66-12 2 24-69 12zm314 0-66-12-2 24 69 12z"
                      fill={palette.deep}
                      fillOpacity=".1"
                    />
                    <path
                      d="M181 227c-5 105-4 202-2 291m160-291c5 105 4 202 2 291"
                      fill="none"
                      stroke={palette.deep}
                      strokeWidth="4"
                      strokeLinecap="round"
                      strokeOpacity=".07"
                    />
                  </g>
                )}
              </svg>
            )}
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}

export function resolveGarmentViewportStyle(
  viewport?: ViewportState,
  origin: { xPct: number; yPct: number } = { xPct: 50, yPct: 50 },
): CSSProperties {
  const zoom = viewport?.zoom ?? 1;
  const panX = viewport?.panPx.x ?? 0;
  const panY = viewport?.panPx.y ?? 0;
  return {
    transformOrigin: `${origin.xPct}% ${origin.yPct}%`,
    transform: `matrix(${zoom}, 0, 0, ${zoom}, ${panX}, ${panY})`,
  };
}

/**
 * Projects the physical torso canvas through the exact transform used by the
 * garment layer. This keeps one millimetre at a stable preview scale while the
 * shirt grows or shrinks behind it.
 */
export function scalePreviewAreaForGarment(
  base: PrintableAreaConfig['previewArea'],
  scale: GarmentVisualScale,
): PrintableAreaConfig['previewArea'] {
  const centreX = base.xPct + base.widthPct / 2;
  const scaledCentreX = 50 + (centreX - 50) * scale.x;
  const widthPct = base.widthPct * scale.x;
  return {
    xPct: scaledCentreX - widthPct / 2,
    yPct: 14 + (base.yPct - 14) * scale.y,
    widthPct,
    heightPct: base.heightPct * scale.y,
  };
}

export interface GarmentViewportProjection {
  previewArea: PrintableAreaConfig['previewArea'];
  viewportOrigin: { xPct: number; yPct: number };
}

/**
 * Reprojects a garment-local print area into a larger editor viewport. The
 * returned negative CSS origin makes the garment and the full-size Fabric
 * canvas share the same viewport matrix, so zoomed artwork is clipped only by
 * the actual editor viewport rather than the smaller unzoomed garment frame.
 */
export function projectGarmentPreviewToViewport(
  base: PrintableAreaConfig['previewArea'],
  garmentRect: { left: number; top: number; width: number; height: number },
  viewportSize: { width: number; height: number },
): GarmentViewportProjection {
  const viewportWidth = Math.max(viewportSize.width, 1);
  const viewportHeight = Math.max(viewportSize.height, 1);
  const garmentWidth = Math.max(garmentRect.width, 1);
  const garmentHeight = Math.max(garmentRect.height, 1);
  return {
    previewArea: {
      xPct: ((garmentRect.left + (base.xPct / 100) * garmentWidth) / viewportWidth) * 100,
      yPct: ((garmentRect.top + (base.yPct / 100) * garmentHeight) / viewportHeight) * 100,
      widthPct: ((base.widthPct / 100) * garmentWidth * 100) / viewportWidth,
      heightPct: ((base.heightPct / 100) * garmentHeight * 100) / viewportHeight,
    },
    viewportOrigin: {
      xPct: (-garmentRect.left / garmentWidth) * 100,
      yPct: (-garmentRect.top / garmentHeight) * 100,
    },
  };
}

function PhotoGarment({
  source,
  colour,
  label,
  priority,
  onError,
}: {
  source: string;
  colour: string;
  label: string;
  priority: boolean;
  onError: () => void;
}) {
  const value = luminance(colour);
  const mask = `url(${JSON.stringify(source)})`;
  const style = {
    '--garment-photo-tone': colour,
    '--garment-photo-texture-opacity': String(0.8 + (1 - value) * 0.16),
    '--garment-photo-depth-opacity': String(value > 0.72 ? 0.3 : 0.19),
    '--garment-photo-fibre-opacity': String(value > 0.72 ? 0.16 : 0.1),
    '--garment-photo-sheen-opacity': String(value > 0.82 ? 0.035 : 0.12 + (1 - value) * 0.08),
    WebkitMaskImage: mask,
    maskImage: mask,
  } as CSSProperties & Record<`--${string}`, string>;

  return (
    <div className="garment-photo" role="img" aria-label={label}>
      <img
        className="garment-photo-source"
        src={source}
        alt=""
        aria-hidden="true"
        onError={onError}
      />
      <div className="garment-photo-cutout" style={style} aria-hidden="true">
        <span className="garment-photo-tone" />
        <img
          className="garment-photo-texture"
          src={source}
          alt=""
          fetchPriority={priority ? 'high' : undefined}
        />
        <span className="garment-photo-depth" />
        <span className="garment-photo-fibres" />
        <span className="garment-photo-sheen" />
      </div>
    </div>
  );
}

function TshirtCollar({ palette, isBack }: { palette: GarmentPalette; isBack: boolean }) {
  if (isBack) {
    return (
      <path
        d="M210 78c15 14 31 20 50 20s35-6 50-20c-8 23-25 34-50 34s-42-11-50-34Z"
        fill={palette.deep}
        stroke={palette.seam}
        strokeWidth="1.2"
      />
    );
  }
  return (
    <path
      d="M210 78c12 27 29 40 50 40s38-13 50-40c-15 11-31 16-50 16s-35-5-50-16Z"
      fill={palette.deep}
      stroke={palette.seam}
      strokeWidth="1.2"
    />
  );
}

function createPalette(base: string): GarmentPalette {
  const light = luminance(base) > 0.72;
  return {
    base,
    edge: mix(base, '#000000', light ? 0.085 : 0.075),
    highlight: mix(base, '#ffffff', light ? 0.1 : 0.045),
    deep: mix(base, '#000000', light ? 0.11 : 0.16),
    outline: mix(base, light ? '#202124' : '#ffffff', light ? 0.3 : 0.12),
    seam: mix(base, light ? '#383a3d' : '#ffffff', light ? 0.25 : 0.18),
  };
}

function mix(source: string, target: string, amount: number): string {
  const from = parseHex(source);
  const to = parseHex(target);
  if (!from || !to) return source;
  const channels = from.map((channel, index) =>
    Math.round(channel + ((to[index] ?? channel) - channel) * amount),
  );
  return `#${channels.map(channel => channel.toString(16).padStart(2, '0')).join('')}`;
}

function luminance(hex: string): number {
  const rgb = parseHex(hex);
  if (!rgb) return 0;
  const [r = 0, g = 0, b = 0] = rgb.map(channel => {
    const value = channel / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function parseHex(hex: string): [number, number, number] | null {
  const value = hex.replace('#', '');
  if (!/^[0-9a-f]{6}$/i.test(value)) return null;
  return [0, 2, 4].map(index => Number.parseInt(value.slice(index, index + 2), 16)) as [
    number,
    number,
    number,
  ];
}
